/**
 * Atlas rewards — игрок получает осознания/духобаллы за первый просмотр
 * каждого открытого элемента в атласе.
 *
 * JB 2026-05-26: «в атласе должны быть какие то награды за первый клик
 * на каждое новое открытое что либо, кристаллы духобаллы за какие то
 * редкие открытия косметика или новые эффекты».
 *
 * Архитектура:
 *   - viewedEntries: persisted Set<string> — все id что игрок уже видел
 *     в атласе. Растёт навсегда.
 *   - claimReward(categoryKey, entryId): pure function — определяет
 *     награду по категории и редкости.
 *   - Бэйджи «новое» показываются на категориях если есть unviewed
 *     unlocked entries.
 *
 * Награды (alpha):
 *   - phrases: первая → +5 осознаний, milestone (10th/100th/1000th) → +20
 *   - lessons: +30 осознаний (или 1 духобалл за rare)
 *   - artifacts: minor → +50 осознаний, major → 1 духобалл
 *   - upgrades: ничего (награда — сам апгрейд)
 *   - whispers: +2 осознания (контент-зрелище, маленькая радость)
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AtlasCategoryKey = 'phrases' | 'lessons' | 'artifacts' | 'upgrades' | 'whispers';

export interface AtlasReward {
  readonly awarenesses: number;
  readonly spirit_balls: number;
}

interface AtlasRewardsStore {
  /** IDs всех уже просмотренных в атласе entry. Растёт. */
  readonly viewedEntries: ReadonlySet<string>;
  /**
   * Когда entry было просмотрено впервые (ms timestamp). Doc 28 §I §5 —
   * нужно для feed «открыто недавно» в Atlas. Растёт навсегда.
   */
  readonly openedAt: ReadonlyMap<string, number>;
  /** Sum lifetime rewards collected (для UI «всего получено»). */
  readonly totalAwarenessesEarned: number;
  readonly totalSpiritBallsEarned: number;

  isViewed(entryId: string): boolean;
  /** Записать что игрок открыл entry. Возвращает true если это первый раз. */
  markViewed(entryId: string, reward: AtlasReward): boolean;
  /** Сброс — admin reset (см. debug). */
  reset(): void;
}

interface PersistedShape {
  readonly viewedEntries: readonly string[];
  readonly openedAt: readonly (readonly [string, number])[];
  readonly totalAwarenessesEarned: number;
  readonly totalSpiritBallsEarned: number;
}

export const useAtlasRewardsStore = create<AtlasRewardsStore>()(
  persist(
    (set, get) => ({
      viewedEntries: new Set(),
      openedAt: new Map(),
      totalAwarenessesEarned: 0,
      totalSpiritBallsEarned: 0,

      isViewed(entryId) {
        return get().viewedEntries.has(entryId);
      },

      markViewed(entryId, reward) {
        const { viewedEntries, openedAt, totalAwarenessesEarned, totalSpiritBallsEarned } = get();
        if (viewedEntries.has(entryId)) return false;
        const nextSet = new Set(viewedEntries);
        nextSet.add(entryId);
        const nextOpened = new Map(openedAt);
        nextOpened.set(entryId, Date.now());
        set({
          viewedEntries: nextSet,
          openedAt: nextOpened,
          totalAwarenessesEarned: totalAwarenessesEarned + reward.awarenesses,
          totalSpiritBallsEarned: totalSpiritBallsEarned + reward.spirit_balls,
        });
        return true;
      },

      reset() {
        set({
          viewedEntries: new Set(),
          openedAt: new Map(),
          totalAwarenessesEarned: 0,
          totalSpiritBallsEarned: 0,
        });
      },
    }),
    {
      name: 'sai-atlas-rewards-v1',
      partialize: (s): PersistedShape => ({
        viewedEntries: Array.from(s.viewedEntries),
        openedAt: Array.from(s.openedAt.entries()),
        totalAwarenessesEarned: s.totalAwarenessesEarned,
        totalSpiritBallsEarned: s.totalSpiritBallsEarned,
      }),
      merge: (persistedState, currentState) => {
        const p = persistedState as Partial<PersistedShape> | undefined;
        return {
          ...currentState,
          viewedEntries: new Set(p?.viewedEntries ?? []),
          openedAt: new Map(p?.openedAt ?? []),
          totalAwarenessesEarned: p?.totalAwarenessesEarned ?? 0,
          totalSpiritBallsEarned: p?.totalSpiritBallsEarned ?? 0,
        };
      },
    },
  ),
);

/**
 * Возвращает последние N entry ids открытых за последние `hoursAgo` часов
 * (по умолчанию 24h). Сортирует от свежих к старым. Doc 28 §I — feed
 * «открыто недавно» в Atlas.
 *
 * 2026-05-26 BUG FIX: `validIds` filter — игнорирует orphan id'ы (entry
 * был удалён из контента, openedAt остался в localStorage forever).
 * Раньше такие id'ы силент'но render'или `null` (`entryById.get(id) ===
 * undefined`), и в feed'е оставались пробелы. Также fetch'аем чуть
 * больше (limit×2) перед filter — компенсируем потенциальные дропы.
 */
export function getRecentlyOpened(
  openedAt: ReadonlyMap<string, number>,
  limit = 5,
  hoursAgo = 24,
  validIds?: ReadonlySet<string>,
): readonly { readonly entryId: string; readonly openedAt: number }[] {
  const cutoff = Date.now() - hoursAgo * 60 * 60 * 1000;
  const entries: { entryId: string; openedAt: number }[] = [];
  for (const [entryId, ts] of openedAt) {
    if (ts < cutoff) continue;
    if (validIds !== undefined && !validIds.has(entryId)) continue;
    entries.push({ entryId, openedAt: ts });
  }
  entries.sort((a, b) => b.openedAt - a.openedAt);
  return entries.slice(0, limit);
}

/**
 * Детерминированно выбирает «фразу дня» из множества unlocked entries.
 * Использует date-based seed чтобы выбор обновлялся раз в сутки и был
 * одинаковым между mount'ами в течение дня. Doc 28 §I.
 *
 * Если unlocked entries пуст — возвращает null (Atlas только что
 * открыт впервые, нечего показывать).
 */
export function pickDailyFeatured<T>(unlockedItems: readonly T[]): T | null {
  if (unlockedItems.length === 0) return null;
  // Date.now() → дата без часов. seed одинаковый на все mount'ы в сутках.
  const now = new Date();
  const dateSeed = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  const idx = dateSeed % unlockedItems.length;
  return unlockedItems[idx] ?? null;
}

/**
 * Награда за первый просмотр entry в атласе. Pure function — зависит от
 * категории и опционально от свойств entry (например phrase.special).
 */
export function computeReward(
  category: AtlasCategoryKey,
  _entryId: string,
  meta?: {
    phraseSpecial?: 'on10thTap' | 'on100thTap' | 'on1000thTap' | undefined;
    artifactWeight?: 'major' | 'minor' | undefined;
  },
): AtlasReward {
  switch (category) {
    case 'phrases': {
      if (meta?.phraseSpecial === 'on1000thTap') {
        return { awarenesses: 0, spirit_balls: 1 };
      }
      if (meta?.phraseSpecial === 'on100thTap') {
        return { awarenesses: 50, spirit_balls: 0 };
      }
      if (meta?.phraseSpecial === 'on10thTap') {
        return { awarenesses: 20, spirit_balls: 0 };
      }
      return { awarenesses: 5, spirit_balls: 0 };
    }
    case 'lessons':
      return { awarenesses: 30, spirit_balls: 0 };
    case 'artifacts': {
      if (meta?.artifactWeight === 'major') {
        return { awarenesses: 0, spirit_balls: 1 };
      }
      return { awarenesses: 50, spirit_balls: 0 };
    }
    case 'upgrades':
      // Награда уже выдана при покупке апгрейда.
      return { awarenesses: 0, spirit_balls: 0 };
    case 'whispers':
      return { awarenesses: 2, spirit_balls: 0 };
  }
}
